/// <reference types="google.maps" />
import { formatAddressDisplay } from '@/features/applicant/utils/addressHelpers'
import { calculatePropertyToEmployerDistance } from './distanceMatrix'
import { waitForGoogleMaps } from './googleMaps'

const DISTANCE_WARNING_THRESHOLD_MILES = 50

export type DistanceCalculationMethod = 'driving' | 'straight_line' | 'none'

export interface EmploymentDistanceResult {
  miles: number | null
  method: DistanceCalculationMethod
}

export interface EmployerAddressFields {
  streetName?: string
  city?: string
  state?: string
  zipCode?: string
  address?: string
}

/** Build property address from application payload (matches Income tab field order). */
export function resolvePropertyAddressFromApplication(
  applicationData: Record<string, unknown> | null | undefined,
): string {
  if (!applicationData) {
    return ''
  }

  const propertyData = (applicationData.property as Record<string, unknown>) || {}

  const propAddress =
    (applicationData.property_address as string) ||
    (applicationData.physicalAddress as string) ||
    (propertyData.property_address as string) ||
    (propertyData.address as string) ||
    ''
  const propCity =
    (applicationData.property_city as string) ||
    (propertyData.property_city as string) ||
    (propertyData.city as string) ||
    ''
  const propState =
    (applicationData.property_state as string) ||
    (propertyData.property_state as string) ||
    (propertyData.state as string) ||
    ''
  const propZipcode =
    (applicationData.property_zipcode as string) ||
    (propertyData.property_zipcode as string) ||
    (propertyData.zipcode as string) ||
    (propertyData.zip_code as string) ||
    ''

  if (!propAddress) {
    return ''
  }

  if (propCity || propState || propZipcode) {
    return `${propAddress} ${propCity} ${propState} ${propZipcode}`.trim()
  }

  return propAddress.trim()
}

/** Build current applicant address from application payload (About tab current_address). */
export function resolveApplicantAddressFromApplication(
  applicationData: Record<string, unknown> | null | undefined,
): string {
  if (!applicationData?.current_address) {
    return ''
  }

  const addr = applicationData.current_address as Record<string, unknown>
  return formatAddressDisplay({
    house_no: (addr.house_no as string) || undefined,
    street: (addr.street as string) || undefined,
    street_type: (addr.street_type as string) || undefined,
    apartment: (addr.apartment as string) || undefined,
    city: (addr.city as string) || undefined,
    state: (addr.state as string) || undefined,
    zip_code: (addr.zip_code as string) || undefined,
    country: (addr.country as string) || undefined,
  }).trim()
}

export interface EmploymentDistanceCheckResult {
  propertyToEmployer: number | null
  applicantToProperty: number | null
  applicantToEmployer: number | null
}

export function anyDistanceRequiresExplanation(
  ...distances: Array<number | null | undefined>
): boolean {
  return distances.some(
    (distance) =>
      distance !== undefined &&
      distance !== null &&
      !Number.isNaN(distance) &&
      distance > DISTANCE_WARNING_THRESHOLD_MILES,
  )
}

export function hasCompleteEmployerAddress(
  employer: EmployerAddressFields,
): boolean {
  return Boolean(
    employer.streetName?.trim() &&
      employer.city?.trim() &&
      employer.state?.trim() &&
      employer.zipCode?.trim(),
  )
}

/** Canonical employer address string used for Distance Matrix (street + city + state + zip). */
export function buildEmployerAddressString(
  employer: EmployerAddressFields,
): string {
  if (hasCompleteEmployerAddress(employer)) {
    return `${employer.streetName} ${employer.city} ${employer.state} ${employer.zipCode}`.trim()
  }

  return employer.address?.trim() || ''
}

/** Build employer address from application detail / API job record field names. */
export function buildEmployerAddressFromJobRecord(job: Record<string, unknown>): string {
  const street =
    (job.company_address as string) ||
    (job.employer_street as string) ||
    ''
  const city =
    (job.company_city as string) ||
    (job.employer_city as string) ||
    ''
  const state =
    (job.company_state as string) ||
    (job.employer_state as string) ||
    ''
  const zip =
    job.company_zipcode != null
      ? `${job.company_zipcode}`
      : job.employer_zip_code != null
        ? `${job.employer_zip_code}`
        : ''

  if (street.trim() && city.trim() && state.trim() && zip.trim()) {
    return `${street} ${city} ${state} ${zip}`.trim()
  }

  const streetAddress =
    (job.employer_street_address as string) ||
    (job.company_address as string) ||
    ''
  return streetAddress.trim()
}

export function normalizeAddressForComparison(address: string): string {
  return address.trim().toLowerCase().replace(/\s+/g, ' ')
}

export function hasValidJobDistance(distance?: number | null): boolean {
  return (
    distance !== undefined && distance !== null && !Number.isNaN(distance)
  )
}

export function parseJobDistanceFromApi(
  value: number | string | null | undefined,
): number | undefined {
  if (typeof value === 'number') {
    return hasValidJobDistance(value) ? value : undefined
  }
  if (typeof value === 'string' && value !== '') {
    const parsed = parseFloat(value)
    return hasValidJobDistance(parsed) ? parsed : undefined
  }
  return undefined
}

export interface EmploymentDistanceRecallFields extends EmployerAddressFields {
  jobDistance?: number
  distanceAddressSnapshot?: string
  address?: string
}

/** True when distance is missing or employer address changed since last distance was set. */
export function needsEmploymentDistanceRecall(
  employer: EmploymentDistanceRecallFields,
): boolean {
  const currentAddress = buildEmployerAddressString(employer)
  if (!currentAddress) {
    return false
  }

  if (!hasValidJobDistance(employer.jobDistance)) {
    return true
  }

  if (!employer.distanceAddressSnapshot) {
    return true
  }

  return (
    normalizeAddressForComparison(currentAddress) !==
    normalizeAddressForComparison(employer.distanceAddressSnapshot)
  )
}

export function distanceAddressSnapshotForEmployer(
  employer: EmployerAddressFields & { address?: string },
): string | undefined {
  const snapshot = buildEmployerAddressString(employer)
  return snapshot || undefined
}

async function geocodeAddress(address: string): Promise<google.maps.LatLng> {
  await waitForGoogleMaps()

  if (!window.google?.maps) {
    throw new Error('Google Maps API not loaded')
  }

  const geocoder = new google.maps.Geocoder()

  return new Promise((resolve, reject) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results?.[0]?.geometry?.location) {
        resolve(results[0].geometry.location)
        return
      }

      reject(new Error(`Geocode failed: ${status}`))
    })
  })
}

async function calculateStraightLineDistance(
  origin: string,
  destination: string,
): Promise<number> {
  await waitForGoogleMaps()

  if (!window.google?.maps?.geometry?.spherical) {
    throw new Error('Google Maps geometry library not loaded')
  }

  const [originLatLng, destinationLatLng] = await Promise.all([
    geocodeAddress(origin),
    geocodeAddress(destination),
  ])

  const meters = google.maps.geometry.spherical.computeDistanceBetween(
    originLatLng,
    destinationLatLng,
  )

  return Math.round((meters / 1609.34) * 100) / 100
}

/**
 * Recalculate property-to-employer distance: driving route first, straight-line fallback.
 */
export async function recalculateEmploymentDistance(
  propertyAddress: string,
  employerAddress: string,
): Promise<EmploymentDistanceResult> {
  if (!propertyAddress?.trim() || !employerAddress?.trim()) {
    return { miles: null, method: 'none' }
  }

  try {
    const miles = await calculatePropertyToEmployerDistance(
      propertyAddress.trim(),
      employerAddress.trim(),
    )
    return { miles, method: 'driving' }
  } catch {
    try {
      const miles = await calculateStraightLineDistance(
        propertyAddress.trim(),
        employerAddress.trim(),
      )
      return { miles, method: 'straight_line' }
    } catch {
      return { miles: null, method: 'none' }
    }
  }
}

/** Calculate all three employment-related address pair distances in parallel. */
export async function recalculateAllEmploymentDistances(
  propertyAddress: string,
  applicantAddress: string,
  employerAddress: string,
): Promise<EmploymentDistanceCheckResult> {
  const [propertyToEmployer, applicantToProperty, applicantToEmployer] =
    await Promise.all([
      propertyAddress.trim() && employerAddress.trim()
        ? recalculateEmploymentDistance(
            propertyAddress.trim(),
            employerAddress.trim(),
          ).then((result) => result.miles)
        : Promise.resolve(null),
      propertyAddress.trim() && applicantAddress.trim()
        ? recalculateEmploymentDistance(
            propertyAddress.trim(),
            applicantAddress.trim(),
          ).then((result) => result.miles)
        : Promise.resolve(null),
      applicantAddress.trim() && employerAddress.trim()
        ? recalculateEmploymentDistance(
            applicantAddress.trim(),
            employerAddress.trim(),
          ).then((result) => result.miles)
        : Promise.resolve(null),
    ])

  return { propertyToEmployer, applicantToProperty, applicantToEmployer }
}

/** Keep prior distance values when a single pair fails to calculate. */
export function mergeCalculatedEmploymentDistances(
  existing: {
    propertyToEmployer?: number | null
    applicantToEmployer?: number | null
    applicantToProperty?: number | null
  },
  calculated: EmploymentDistanceCheckResult,
): {
  propertyToEmployer?: number
  applicantToEmployer?: number
  applicantToProperty?: number
} {
  return {
    propertyToEmployer:
      calculated.propertyToEmployer ?? existing.propertyToEmployer ?? undefined,
    applicantToEmployer:
      calculated.applicantToEmployer ?? existing.applicantToEmployer ?? undefined,
    applicantToProperty:
      calculated.applicantToProperty ?? existing.applicantToProperty ?? undefined,
  }
}
